// src/components/map/hooks/adapters/useLocalNetworkMap.ts
//
// Sub-hook for useFacilityHighlightAdapter.
// Manages the well-to-facility network map visualization: rendering network
// lines with depth-based colors, highlighting network facilities, managing
// the network options panel state, and the network depth legend.
//
// The consuming adapter is responsible for composing this with the click
// handler (which owns clickedEntity) and the contribution highlight sub-hook.
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { feature, featureCollection } from "@turf/helpers";
import colormap from "colormap";
import type { AnyLayer } from "mapbox-gl";
import { useMapStore } from "stores/map";
import { selectNetworkDepthLegend } from "stores/map/mapSelectors";
import { MapInteraction } from "types/maps/interactions";

import { FACILITY_LAYER, FACILITY_TYPE_ENUM } from "../../../../constants";
import { panToNetworkBounds } from "../../toolbar/midstream-navigator/hooks/util";
import { clearGeojsonSource, setGeojsonSource } from "../../utils/geojsonSource";
import { safeRemoveLayerAndSource } from "../../utils/layerOperations";
import {
  HIGHLIGHTED_FACILITY,
  HIGHLIGHTED_NETWORK_FACILITIES,
  NETWORK_DEPTH_LEGEND_TITLE,
  WELL_NETWORK_MAP
} from "../../utils/networkLayerConstants";
import { WellNetworkRequest, useGetWellNetworkMap } from "../useGetWellNetworkMap";
import { useMapContext } from "../useMapContext";
import { useMapDispatch } from "../useMapDispatch";
import useMutateFacilitiesEnvelope from "../useMutateFacilitiesEnvelope";
import type { ClickedEntity } from "./useFacilityClickHandler";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseLocalNetworkMapParams {
  /** The currently clicked entity (from useFacilityClickHandler). */
  readonly clickedEntity: Maybe<ClickedEntity>;
}

export interface UseLocalNetworkMapReturn {
  /** Current well network map request parameters. */
  readonly wellNetworkMapRequest: WellNetworkRequest;
  /** Update the well network map request. */
  readonly setWellNetworkMapRequest: (request: WellNetworkRequest) => void;
  /** Network line width range [min, max]. */
  readonly networkLineWidth: readonly [number, number];
  /** Update the network line width. */
  readonly setNetworkLineWidth: (width: [number, number]) => void;
  /** Whether the network options panel should be visible. */
  readonly showNetworkVisualOptions: boolean;
  /** Well network map data from React Query. */
  readonly wellNetworkMap: ReturnType<typeof useGetWellNetworkMap>["data"];
  /** Whether the network map data is currently fetching. */
  readonly isNetworkMapFetching: boolean;
  /** Trigger a well network map display for the clicked well. */
  readonly showWellNetworkMap: () => void;
  /** Clear all network map layers, sources, and related state. */
  readonly clearNetworkMap: () => void;
  /** The network depth legend state from the store. */
  readonly networkDepthLegend: ReturnType<
    typeof useMapStore<ReturnType<typeof selectNetworkDepthLegend>>
  >;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Sub-hook that manages the well-to-facility network map visualization.
 *
 * Responsibilities:
 * - Manages network map request state and React Query data fetching
 * - Renders network line layers with depth-based coloring and fraction-based width
 * - Highlights network facilities with depth-based icon coloring
 * - Manages the network options panel visibility
 * - Manages the network depth legend via Zustand store
 * - Pans the map to network bounds after rendering
 */
export function useLocalNetworkMap(
  params: UseLocalNetworkMapParams
): UseLocalNetworkMapReturn {
  const { clickedEntity } = params;

  // ── Map context ────────────────────────────────────────────────────

  const { mapbox, isMapInitialized } = useMapContext();

  // ── Store reads / actions ──────────────────────────────────────────

  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);
  const clearNetworkDepthLegend = useMapStore((s) => s.clearNetworkDepthLegend);
  const setNetworkDepthLegend = useMapStore((s) => s.setNetworkDepthLegend);
  const mapDispatch = useMapDispatch();

  // ── Mutations ──────────────────────────────────────────────────────

  const { mutateAsync: getFacilitiesEnvelope } = useMutateFacilitiesEnvelope();

  // ── Local state ────────────────────────────────────────────────────

  const [wellNetworkMapRequest, setWellNetworkMapRequest] = useState<WellNetworkRequest>({
    wellIds: [],
    date: null,
    onlyPrimaryPath: true
  });
  const [networkLineWidth, setNetworkLineWidth] = useState<[number, number]>([3, 12]);
  const [networkMapCleared, setNetworkMapCleared] = useState(false);
  const [showNetworkVisualOptions, setShowNetworkVisualOptions] = useState(false);

  // ── React Query ────────────────────────────────────────────────────

  const { data: wellNetworkMap, isFetching: isNetworkMapFetching } =
    useGetWellNetworkMap(wellNetworkMapRequest);

  // ── Actions ────────────────────────────────────────────────────────

  const showWellNetworkMap = useCallback(() => {
    setNetworkMapCleared(false);
    if (!clickedEntity?.well) return;
    setWellNetworkMapRequest({
      ...wellNetworkMapRequest,
      wellIds: [clickedEntity.well.wellId]
    });
  }, [clickedEntity?.well, wellNetworkMapRequest]);

  const clearNetworkMap = useCallback(() => {
    clearNetworkDepthLegend();
    setShowNetworkVisualOptions(false);
    setNetworkMapCleared(true);
    clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
    clearGeojsonSource(mapbox, WELL_NETWORK_MAP);

    safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);

    setWellNetworkMapRequest({ wellIds: [], date: null, onlyPrimaryPath: true });
  }, [mapbox, clearNetworkDepthLegend]);

  // ── Effect: loading state for network map ──────────────────────────

  useEffect(() => {
    mapDispatch({
      type: "update",
      payload: { isLoading: isNetworkMapFetching }
    });
  }, [isNetworkMapFetching, mapDispatch]);

  // ── Effect: render network map when data arrives ───────────────────

  useEffect(() => {
    if (!mapbox || !wellNetworkMap || networkMapCleared) return;

    const keys = Object.keys(wellNetworkMap.wellFeatureCollections);
    if (keys.length === 0) {
      clearGeojsonSource(mapbox, WELL_NETWORK_MAP);
      clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
      clearNetworkDepthLegend();

      safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);

      setShowNetworkVisualOptions(false);
      setWellNetworkMapRequest({ wellIds: [], date: null, onlyPrimaryPath: true });
      toast.warn("No facility network found for selected well.");
      return;
    }

    setShowNetworkVisualOptions(true);

    const maxDepth = wellNetworkMap.maxDepth;
    const colors = colormap({
      colormap: "autumn",
      nshades: Math.max(3, maxDepth + 1),
      format: "hex",
      alpha: 1
    });

    const geojson = wellNetworkMap.wellFeatureCollections[keys[0]];
    const depthAndColors = colors
      .map((color: string, index: number) => [index, color])
      .flat();

    const lineColorStyle = ["match", ["get", "depth"], ...depthAndColors, "#000000"];
    const lineWidthStyle = [
      "interpolate",
      ["linear"],
      ["get", "fraction"],
      0.01,
      networkLineWidth[0],
      1.0,
      networkLineWidth[1]
    ];

    setNetworkDepthLegend({
      title: NETWORK_DEPTH_LEGEND_TITLE,
      legendItems: colors.map((color: string, index: number) => ({
        color,
        title: index.toString()
      })),
      visible: true
    });

    if (!setGeojsonSource(mapbox, WELL_NETWORK_MAP, geojson)) {
      const layer = {
        id: WELL_NETWORK_MAP,
        type: "line",
        source: { type: "geojson", data: geojson },
        paint: {
          "line-color": lineColorStyle,
          "line-width": lineWidthStyle
        }
      } as AnyLayer;
      mapbox.addLayer(layer);
    } else {
      mapbox.setPaintProperty(WELL_NETWORK_MAP, "line-width", lineWidthStyle);
      mapbox.setPaintProperty(WELL_NETWORK_MAP, "line-color", lineColorStyle);
    }

    // Highlight network facilities
    const highlightFacilities = async () => {
      if (!geojson?.features?.length) return;

      try {
        const facilityIds = new Set<string>();
        const facilityToDepthMap = new Map<string, number>();
        let isFirstFeature = true;
        let wellId = "";

        geojson.features.forEach((f) => {
          if (f.properties) {
            const depth = f.properties.depth ?? 0;

            if (f.properties.to) {
              facilityIds.add(f.properties.to);
              facilityToDepthMap.set(f.properties.to, depth);
            }

            if (f.properties.from && !isFirstFeature) {
              facilityIds.add(f.properties.from);
              facilityToDepthMap.set(f.properties.from, depth);
            } else if (f.properties.from && isFirstFeature) {
              wellId = f.properties.from;
            }

            isFirstFeature = false;
          }
        });

        const facilityIdArray = Array.from(facilityIds);
        if (facilityIdArray.length === 0) {
          clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
          return;
        }

        const highlightFacilityIcons = async () => {
          await new Promise<void>((resolve) => {
            if (mapbox.isStyleLoaded() && mapbox.loaded()) {
              mapbox.once(MapInteraction.Idle, () => resolve());
            } else {
              resolve();
            }
          });

          const foundFacilities = mapbox.querySourceFeatures(FACILITY_LAYER, {
            sourceLayer: "eva-facility",
            filter: [
              "has",
              ["get", "Uwi"],
              [
                "literal",
                facilityIdArray.reduce((obj, id) => {
                  obj[id] = true;
                  return obj;
                }, {} as Record<string, boolean>)
              ]
            ]
          });

          if (foundFacilities.length > 0) {
            const highlightedFeatures = foundFacilities.map((feat) => {
              const facilityId = feat.properties?.Uwi;
              const depth = facilityToDepthMap.get(facilityId) ?? 0;
              return feature(feat.geometry, {
                ...feat.properties,
                highlighted: true,
                networkDepth: depth
              });
            });

            const highlightedFacilitiesCollection =
              featureCollection(highlightedFeatures);

            safeRemoveLayerAndSource(mapbox, HIGHLIGHTED_NETWORK_FACILITIES);

            const facilityDepthColors = colors
              .map((color: string, index: number) => [index, color])
              .flat();

            mapbox.addSource(HIGHLIGHTED_NETWORK_FACILITIES, {
              type: "geojson",
              data: highlightedFacilitiesCollection
            });

            mapbox.addLayer({
              id: HIGHLIGHTED_NETWORK_FACILITIES,
              type: "symbol",
              source: HIGHLIGHTED_NETWORK_FACILITIES,
              paint: {
                "icon-color": [
                  "match",
                  ["get", "networkDepth"],
                  ...facilityDepthColors,
                  colors[0] || "#FF0000"
                ]
              },
              layout: {
                "icon-image": [
                  "match",
                  ["get", "facilityType"],
                  "CompressorStation",
                  "compressor-station",
                  FACILITY_TYPE_ENUM.GasPlant,
                  "gas-plant",
                  "Battery",
                  "battery",
                  "MeteringStation",
                  "metering-station",
                  "WastePlant",
                  "waste-plant",
                  "InjectionDisposalFacility",
                  "injection-facility",
                  "GatheringSystem",
                  "gathering-system",
                  /* default */ "default-facility"
                ],
                "icon-size": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  6,
                  0.2,
                  10,
                  1.0,
                  12,
                  1.5,
                  22,
                  2.0
                ],
                "icon-allow-overlap": true,
                "icon-ignore-placement": true
              }
            });
          }
        };

        const envelope = await getFacilitiesEnvelope({
          wellId,
          facilityIds: facilityIdArray
        });

        await panToNetworkBounds(wellId, facilityIdArray, mapbox, envelope);
        highlightFacilityIcons();
      } catch {
        toast.error("Error highlighting network facilities");
        clearGeojsonSource(mapbox, HIGHLIGHTED_FACILITY);
      }
    };

    highlightFacilities();
  }, [
    wellNetworkMap,
    networkLineWidth,
    mapbox,
    isMapInitialized,
    getFacilitiesEnvelope,
    clearNetworkDepthLegend,
    setNetworkDepthLegend,
    networkMapCleared
  ]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    wellNetworkMapRequest,
    setWellNetworkMapRequest,
    networkLineWidth,
    setNetworkLineWidth,
    showNetworkVisualOptions,
    wellNetworkMap,
    isNetworkMapFetching,
    showWellNetworkMap,
    clearNetworkMap,
    networkDepthLegend
  };
}
